/**
 * How the bench is projected, in one place because EVERY object on it has to
 * agree.
 *
 * The viewer stands a little above the bench rather than level with it, so
 * anything lying flat — a vessel's mouth, the liquid in it, a wire gauze, a
 * burner's base, the ring of a clamp — is foreshortened rather than collapsed
 * to a line. The rule is not "ellipses are allowed", it is that there is ONE
 * eye height: an ellipse on the glassware and a straight line on the apparatus
 * beside it is what reads as a broken 3D render, and for a while that is
 * exactly what the bench was.
 *
 * Lives apart from vessel2D.ts because it is not about vessels. Stands2D.tsx
 * draws no profiles and still needs every one of these.
 */

/** A horizontal circle's drawn height as a fraction of its width. 0 would be
 *  a strict side elevation — the eye exactly at bench level. */
export const VIEW_TILT = 0.28;

const f = (n: number): string => n.toFixed(1);

/** A full ellipse, as a path — usable as a fill, a clip or a stroke, which a
 *  bare <ellipse> element is not when it has to be composed with others. */
export function ellipsePath(cy: number, rx: number, ry: number): string {
  return `M ${f(-rx)},${f(cy)} A ${f(rx)},${f(ry)} 0 0 1 ${f(rx)},${f(cy)} A ${f(rx)},${f(ry)} 0 0 1 ${f(-rx)},${f(cy)} Z`;
}

/** Just the near half — the edge of a circle that faces the viewer. */
export function frontArcPath(cy: number, rx: number, ry: number): string {
  return `M ${f(-rx)},${f(cy)} A ${f(rx)},${f(ry)} 0 0 0 ${f(rx)},${f(cy)}`;
}

/** Just the far half — the edge that runs behind whatever sits in the circle. */
export function backArcPath(cy: number, rx: number, ry: number): string {
  return `M ${f(-rx)},${f(cy)} A ${f(rx)},${f(ry)} 0 0 1 ${f(rx)},${f(cy)}`;
}

/** A short upright cylinder — a flask's ground-glass collar, a burner's brass
 *  one. Its silhouette is the far half of the top circle, the two sides, and
 *  the near half of the bottom one. */
export function bandPath(cyTop: number, cyBottom: number, rx: number, ry: number): string {
  return `M ${f(-rx)},${f(cyTop)} A ${f(rx)},${f(ry)} 0 0 1 ${f(rx)},${f(cyTop)}`
    + ` L ${f(rx)},${f(cyBottom)} A ${f(rx)},${f(ry)} 0 0 1 ${f(-rx)},${f(cyBottom)} Z`;
}

/** A disc lying flat with thickness — a burner's base, a stand's foot. The top
 *  face is the whole ellipse; the rim below it is what gives it height. */
export function discPath(cy: number, rx: number, thickness: number): string {
  const ry = rx * VIEW_TILT;
  return bandPath(cy - thickness, cy, rx, ry);
}
